import uniqBy from 'lodash/uniqBy';
import { getMemberSettingForSimpleRules } from '.';
import {
  ConditionTextMap,
  DepartmentAvatar,
  canOnlyPostSetting,
} from '../../controllers/flows/FlowsShareSheetController/ShareSheetModalController/data';
import {
  SimplifiedShareSheetCardData,
  SimplifiedShareSheetDataTypes,
} from '../../controllers/flows/FlowsShareSheetController/types';
import {
  CriteriaValueItem,
  CriteriaRule,
  CriteriaGroups,
} from '../../interfaces/Flow/Builder';

const getValue = (currentValue: CriteriaValueItem, rule: CriteriaRule) => {
  if (rule.field === 'manager') {
    return currentValue.value === 'memberIsManager';
  } else {
    if (rule.field === 'member' || rule.field === 'directReport') {
      return [currentValue.id];
    } else {
      return [currentValue.value];
    }
  }
};

const getRules = (criteria: CriteriaGroups) => {
  return criteria.groups?.map((criteriaGroup) => {
    return {
      condition: criteriaGroup.groupCondition,
      rules: (criteriaGroup.groupRules || []).flatMap((rule) =>
        rule.value.map((currentValue) => ({
          field: rule.field,
          value: getValue(currentValue, rule),
          operator: rule.operator,
        })),
      ),
    };
  });
};

const getAutoCompleteOptionsForDepartmentsOnlyFromCriteria = (
  criteria: CriteriaGroups,
) => {
  const departments: SimplifiedShareSheetCardData[] = [];

  criteria.groups?.forEach((criteriaGroup) => {
    const hasDepartmentRule = criteriaGroup.groupRules?.some((rule) => {
      return rule.field === 'department';
    });

    if (hasDepartmentRule) {
      criteriaGroup.groupRules.forEach((rule) => {
        if (rule.field === 'department') {
          rule.value.forEach((currentValue) => {
            departments.push({
              id: currentValue.id,
              title: `Department ${ConditionTextMap[rule.operator]} ${
                currentValue.value
              }`,
              type: SimplifiedShareSheetDataTypes.Department,
              avatar: DepartmentAvatar,
              setting: canOnlyPostSetting,
              email: 'Loading members',
              condition: rule.operator,
            });
          });
        }
      });
    }
  });

  return departments;
};

const getRulesForMembersOnly = (criteria: CriteriaGroups) => {
  return criteria.groups
    ?.filter((criteriaGroup) => {
      return criteriaGroup.groupRules?.some((rule) => rule.field === 'member');
    })
    .flatMap((criteriaGroup) => {
      const memberRules = criteriaGroup.groupRules?.filter(
        (rule) => rule.field === 'member',
      );
      if (!memberRules) return [];

      const rules = memberRules.flatMap((rule) => {
        return rule.value.map((value) => {
          return {
            field: rule.field,
            value: getValue(value, rule),
            operator: rule.operator,
          };
        });
      });

      return [
        {
          condition: criteriaGroup.groupCondition,
          rules,
        },
      ];
    });
};

export const mapRulesFromCriteriaGroupsAndReturnMembersOnly = (
  criteriaGroups: CriteriaGroups | undefined,
  noSkippedMembers?: boolean,
  canIncludeNewInviteMembers = true,
) => {
  if (criteriaGroups) {
    if (criteriaGroups && !criteriaGroups.groups.length) {
      return undefined;
    }

    if (
      criteriaGroups.groups.length === 1 &&
      criteriaGroups.groups[0].groupRules.length === 1 &&
      Array.isArray(criteriaGroups.groups[0].groupRules[0].value) &&
      criteriaGroups.groups[0].groupRules[0].value.some(
        (x) => x.id === 'everyone',
      )
    ) {
      return undefined;
    }
    const rules = getRulesForMembersOnly(criteriaGroups);

    if (rules.length > 0) {
      const customCriteria = {
        rules,
        condition: criteriaGroups.groupsCondition,
      };

      return {
        criteria: {
          custom: { ...customCriteria },
        },
        ...(canIncludeNewInviteMembers && { isNewMembersAdded: true }),
        ...(!noSkippedMembers && { skippedMembers: [] }),
      };
    } else {
      return undefined;
    }
  }

  return undefined;
};

const isEveryone = (criteria: CriteriaGroups | undefined) =>
  criteria &&
  criteria.groups.length === 1 &&
  criteria.groups[0].groupRules.length === 1 &&
  Array.isArray(criteria.groups[0].groupRules[0].value) &&
  criteria.groups[0].groupRules[0].value.some((x) => x.id === 'everyone');

const isDepartmentPresent = (
  { id }: SimplifiedShareSheetCardData,
  departments: SimplifiedShareSheetCardData[],
) => departments.some(({ id: deptId }) => id === deptId);

export const mapRulesFromCriteriaGroupsAndReturnDepartmentsOnly = (
  participationCriteria: CriteriaGroups | undefined,
  visibilityCriteria: CriteriaGroups | undefined,
  totalMembers = 0,
) => {
  const shouldReturnEmpty =
    !participationCriteria ||
    !participationCriteria.groups.length ||
    !visibilityCriteria ||
    !visibilityCriteria.groups.length;

  if (shouldReturnEmpty) {
    return [];
  }

  const everyone: SimplifiedShareSheetCardData[] = [];
  const isEveryoneAParticipant = isEveryone(participationCriteria);
  const isEveryoneAViewer = isEveryone(visibilityCriteria);

  if (isEveryoneAParticipant || isEveryoneAViewer) {
    everyone.push({
      id: 'everyone',
      title: 'Everyone in this workspace',
      setting: getMemberSettingForSimpleRules({
        isMemberAParticipant: isEveryoneAParticipant || false,
        isMemberHavingVisibility: isEveryoneAViewer || false,
        isMemberAFlowOwner: false,
      }),
      icon: 'everyone',
      email: `${totalMembers} members`,
      type: SimplifiedShareSheetDataTypes.Everyone,
    });
  }

  const departmentsWhoAreParticipants =
    getAutoCompleteOptionsForDepartmentsOnlyFromCriteria(participationCriteria);
  const departmentsWhoAreViewers =
    getAutoCompleteOptionsForDepartmentsOnlyFromCriteria(visibilityCriteria);

  const departments = uniqBy(
    [...departmentsWhoAreParticipants, ...departmentsWhoAreViewers],
    'id',
  ).map((department) => ({
    ...department,
    setting: getMemberSettingForSimpleRules({
      isMemberAParticipant: isDepartmentPresent(
        department,
        departmentsWhoAreParticipants,
      ),
      isMemberHavingVisibility: isDepartmentPresent(
        department,
        departmentsWhoAreViewers,
      ),
      isMemberAFlowOwner: false,
    }),
  }));

  if (departments.length > 0 || everyone.length > 0) {
    return [...departments, ...everyone];
  }

  return [];
};

export const mapRulesFromCriteriaGroups = (
  criteriaGroups: CriteriaGroups | undefined,
  noSkippedMembers?: boolean,
  canIncludeNewInviteMembers = true,
) => {
  if (criteriaGroups) {
    if (criteriaGroups && !criteriaGroups.groups.length) {
      return null;
    }

    if (
      criteriaGroups.groups.length === 1 &&
      criteriaGroups.groups[0].groupRules.length === 1 &&
      Array.isArray(criteriaGroups.groups[0].groupRules[0].value) &&
      criteriaGroups.groups[0].groupRules[0].value.findIndex(
        (x) => x.id === 'everyone',
      ) > -1
    ) {
      return {
        criteria: {
          everyone: true,
        },
        ...(canIncludeNewInviteMembers && { isNewMembersAdded: true }),
        ...(!noSkippedMembers && { skippedMembers: [] }),
      };
    }

    const customCriteria = {
      rules: getRules(criteriaGroups),
      condition: criteriaGroups.groupsCondition,
    };

    return {
      criteria: {
        custom: { ...customCriteria },
      },
      ...(canIncludeNewInviteMembers && { isNewMembersAdded: true }),
      ...(!noSkippedMembers && { skippedMembers: [] }),
    };
  }

  return null;
};
